import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, first, map, of, takeUntil, tap } from 'rxjs';
import { AuthHttpService } from './auth-http.service';
import { LocalStorageService } from './local-storage.service';
import { AuthResult, User, UserLoginDto, getAccessToken } from '../utils/auth.utils';
import { Identifier } from '../models/storage.model';
import { SessionService } from './session.service';
import { NavigationEnd, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DynamicLoaderService } from 'app/shared/dynamic/services/dynamic-loader.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  baseUrl = environment.apiUrl;

  userAuthenticationChanged$?: Observable<User | undefined>;

  private userAuthenticationChangedSub = new BehaviorSubject<User | undefined>(undefined);
  private destroyed: Subject<void> = new Subject();
  private jwtHelper = new JwtHelperService();

  constructor(
    private authHttpService: AuthHttpService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private sessionService: SessionService,
    private dynamicLoaderService: DynamicLoaderService
  ) {
    this.userAuthenticationChanged$ = this.userAuthenticationChangedSub.asObservable();
    this.subscribeToQueryParamChange();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
  }

  loginUserByNonceId(nonceId: string): Observable<AuthResult> {
    return this.authHttpService
      .loginByNonce(nonceId)
      .pipe(tap(res => this.handleLoginResponse(res)));
  }

  loginUser(login: UserLoginDto): Observable<AuthResult> {
    return this.authHttpService.loginUser(login).pipe(tap(res => this.handleLoginResponse(res)));
  }

  private handleLoginResponse(res: AuthResult): void {
    this.processToken(res.token);

    this.localStorageService.setStorageItem(
      Identifier.TYPE_VISIBLE_MENUS,
      JSON.stringify(res.visibleMenuIds)
    );

    this.localStorageService.setStorageItem(
      Identifier.TYPE_ENABLED_ACTIONS,
      JSON.stringify(res.enabledActionsIds)
    );
  }

  logout(): void {
    this.cleanUp();
    this.sessionService.endSession();
  }

  private subscribeToQueryParamChange(): void {
    this.router.events
      .pipe(
        first((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => this.authenticateByLocalStorage()),
        takeUntil(this.destroyed)
      )
      .subscribe(token => {
        if (token) {
          this.processToken(token);
        } else {
          if (!this.router.url.includes('login-local')) {
            this.sessionService.endSession();
          }
        }
      });
  }

  authenticateByLocalStorage(): string | undefined {
    const storageToken = localStorage.getItem(Identifier.TYPE_TOKEN) as string;
    if (this.isTokenValid(storageToken)) {
      return storageToken;
    } else {
      return undefined;
    }
  }

  authenticateByNonce(nonce: string): Observable<AuthResult | undefined> {
    if (nonce) {
      return this.authHttpService.loginByNonce(nonce).pipe(
        map(authResult => {
          if (this.isTokenValid(authResult?.token)) {
            this.localStorageService.setStorageItem(
              Identifier.TYPE_VISIBLE_MENUS,
              JSON.stringify(authResult?.visibleMenuIds)
            );
            this.localStorageService.setStorageItem(
              Identifier.TYPE_ENABLED_ACTIONS,
              JSON.stringify(authResult?.enabledActionsIds)
            );
            return authResult;
          } else {
            return undefined;
          }
        })
      );
    } else {
      return of(undefined);
    }
  }

  isTokenValid(token: string | undefined): boolean {
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  confirmCurrentTokenValidity(): boolean {
    const token = getAccessToken();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  processToken(token: string): User {
    const user = this.getUserFromToken(token);

    this.localStorageService.setStorageItem(Identifier.TYPE_TOKEN, token);
    this.localStorageService.setStorageItem(Identifier.TYPE_USER, JSON.stringify(user));

    // this.store.dispatch(setUserPhoto({ photoUrl: user.photoFileUri }));

    this.userAuthenticationChangedSub.next(user);

    return user;
  }

  private getUserFromToken(token: string): User {
    const decodedToken = this.jwtHelper.decodeToken(token);
    return {
      userId: decodedToken.nameid,
      email: decodedToken.email,
      unique_name: decodedToken.unique_name,
      // role: decodedToken.role,
      // permissions: decodedToken.permissions,
      fullName: decodedToken.fullName,
      entityId: +decodedToken.entityId,
      sysEntityTypeId: +decodedToken.sysEntityTypeId,
      // photoFileUri: decodedToken.photoFileUri,
      // correspondenceLanguageId: +decodedToken.correspondenceLanguageId,
    };
  }

  private cleanUp(): void {
    this.localStorageService.removeStorageItem(Identifier.TYPE_TOKEN);
    this.localStorageService.removeStorageItem(Identifier.TYPE_USER);
    this.localStorageService.removeStorageItem(Identifier.TYPE_VISIBLE_MENUS);
    this.localStorageService.removeStorageItem(Identifier.TYPE_ENABLED_ACTIONS);
    this.userAuthenticationChangedSub.next(undefined);
    this.dynamicLoaderService.removeAllDynamicComponents();
  }
}
